import { useUsersStore, useAuthStore, useGeolocationStore, useGlobalStore, usePermissionsStore } from 'src/stores/index.js'
import { LocalStorage } from 'quasar'
import UsersService from 'src/services/users'
import PermissionsService from 'src/services/permissions'
import BiometricsService from 'src/services/biometrics'
import { i18n } from 'src/boot/i18n'
import { refreshRemoteOfflineFlag } from 'src/offline/remote-flag'
import { organizationIdOf } from '@/utils/user'
import { unwatchMockPolicyOnResume, watchMockPolicyOnResume } from '@/geolocation/mock-policy-resume'
// Capa offline (SQLite): el outbox tiene su PROPIA pausa de auth (authPaused en sync/outbox.js).
// Un 401 de fondo en el worker setea authPaused=true y ningún drain corre hasta reanudar. Importamos
// el resume + el nudge de sync para reanudar y empujar la cola pendiente al reingresar (sin esto, tras
// re-login los cambios en SQLite no drenan hasta recargar la página).
import { resumeAuth } from 'src/offline/sync/outbox'
import { syncNow, startSyncWorker, stopSyncWorker } from 'src/offline/sync/sync-worker'
import { wipeCatalogsForUserChange } from 'src/offline/domain/catalogs'
import { writeBootCurrentCache, clearBootCurrentCache } from 'src/stores/users/bootCache'

/**
 * Offline (auth-resume): si un sync de fondo recibió 401, el outbox quedó pausado
 * (authPaused=true) pidiendo reingresar. Al reingresar con éxito hay que LIMPIAR esa
 * pausa y empujar la cola para que drene sin recargar la página.
 * Best-effort (fire-and-forget, NUNCA bloquea ni rompe el login). Online sin pausa =
 * inocuo (resumeAuth limpia un flag que ya estaba en false y syncNow no encuentra nada
 * que adelantar).
 */
const resumeOfflineAuthOnLogin = () => {
  try {
    // POST-LOGIN: ensureOfflineBooted es idempotente (el cold boot ya arrancó la capa; aquí es no-op).
    // Import DINÁMICO para no crear ciclo (boot/offline -> stores/index -> auth/actions). Lo que importa
    // de este camino es re-arrancar el worker (el logout lo detiene), reanudar la pausa por 401 y
    // nudgear.
    import('src/boot/offline')
      .then(m => (m.ensureOfflineBooted ? m.ensureOfflineBooted() : undefined))
      .then(() => {
        // Re-arranca el worker periódico: logout lo detiene (stopSyncWorker); startSyncWorker es
        // idempotente. El drain filtra el outbox por user_id (solo sincroniza las ops de este usuario).
        startSyncWorker()
        resumeAuth()
        return syncNow()
      })
      .catch(() => { /* best-effort */ })
  } catch { /* best-effort: nunca rompe el login */ }
}

// Distingue un rechazo real del grant (4xx: refresh_token invalido, revocado o expirado) de una
// falla transitoria (red, timeout, 5xx). Solo el primero significa que reintentar no va a
// funcionar solo con reintentar; ver loginWithBiometrics.
function isCredentialGrantError (settledResult) {
  const reason = settledResult.reason || {}
  // El status puede venir en dos formas y hay que aceptar ambas:
  //   - `reason.response.status`: el error de axios crudo, si nadie intercepto.
  //   - `reason.status`: lo que rechaza src/api/responser.js, que hace `Promise.reject(error)`
  //     donde `error` ES `response.response`, o sea el objeto de respuesta (status en la raiz,
  //     sin `.response`). Este es el caso real en la app: solo mirar `reason.response.status`
  //     dejaba esta funcion devolviendo siempre false y volvia inalcanzable la rama de
  //     re-enrolamiento. Verificado en dispositivo con un 401 del grant de GraphQL.
  const status = (reason.response && reason.response.status) || reason.status
  return typeof status === 'number' && status >= 400 && status < 500
}

// El grant de refresh_token de GraphQL va cifrado con APP_KEY, y los nodos detras del load
// balancer pueden tener valores de APP_KEY distintos (bug de infra reportado en TKT-2041 el
// 3-ago, sin resolver al 16-ago): el MISMO refresh_token responde 401 en un nodo y 200 en el
// siguiente. League/oauth2-server manda ese caso puntual por invalidRefreshToken() con el hint
// literal 'Cannot decrypt the refresh token' (ver RefreshTokenGrant::validateOldRefreshToken en
// binnacle-graphql-api), el mismo 401/invalid_request que un rechazo real (token expirado o
// revocado). No hay forma de diferenciarlos por status; solo el hint los distingue.
//
// Tratar este hint puntual como rechazo real apaga el biometrico de gente cuya credencial nunca
// estuvo mal, solo pidieron al nodo equivocado: verificado en emulador contra produccion, 3 de 8
// intentos fallaron con este hint exacto y los otros 5 con el MISMO refresh_token entraron bien.
// Mientras el infra no sincronice APP_KEY, esto se trata como falla transitoria (misma rama que
// un 503): se reintenta el par rotado sin perder el enrolamiento, en vez de forzar reingresar con
// contrasena. "Token has expired" / "Token has been revoked" / etc. si son rechazos reales.
function isTransientRefreshTokenDecryptError (settledResult) {
  const reason = settledResult.reason || {}
  const data = reason.data || (reason.response && reason.response.data) || {}
  return data.hint === 'Cannot decrypt the refresh token'
}

// Persiste los access tokens de ambos emisores y marca la sesion como exitosa. Es la unica parte
// que login() (password) y applyAuthResponses (refresh_token) hacen de forma identica; el resto
// del flujo difiere y se queda en cada caller a proposito.
function setAccessTokens (context, apiResponse, gqlResponse) {
  const access_token = apiResponse.access_token
  const gql_access_token = gqlResponse.access_token

  LocalStorage.set('access_token', access_token)
  LocalStorage.set('gql_access_token', gql_access_token)

  context.status = 'success'
  context.access_token = access_token
  context.gql_access_token = gql_access_token
}

// Serializa el par de refresh tokens en el keystore, detras del biometrico. Es el unico formato
// que entiende loginWithBiometrics al releerlo, asi que vive en un solo lugar: si el shape cambia
// aqui, cambia para los cuatro puntos que enrolan o re-guardan.
//
// Si la escritura falla, se borra la entrada y se reintenta UNA vez. En iOS el llavero sobrevive a
// desinstalar la app, asi que una entrada vieja deja al plugin en un estado sin salida: SecItemAdd
// responde errSecDuplicateItem y el SecItemUpdate que hace en consecuencia responde
// errSecItemNotFound, con lo que el enrolamiento truena para siempre y no hay forma de recuperarse
// desde la app. Reproducido en el simulador; se resolvia limpiando el llavero a mano.
async function persistBiometricTokens (username, refreshToken, gqlRefreshToken) {
  const blob = JSON.stringify({
    refresh_token: refreshToken,
    gql_refresh_token: gqlRefreshToken
  })

  try {
    await BiometricsService.setCredentials(username, blob)
  } catch (error) {
    console.error(error)
    // Borrar puede fallar tambien (p.ej. no habia nada que borrar): no debe tapar el error real
    // de la escritura, que es el que interesa si el reintento tampoco funciona.
    try {
      await BiometricsService.deleteCredentials()
    } catch (deleteError) {
      console.error(deleteError)
    }
    await BiometricsService.setCredentials(username, blob)
  }
}

const BIOMETRIC_ENROLL_DISMISSED_KEY = 'biometric_enroll_dismissed_users'

function dismissedEnrollmentUserIds () {
  const ids = LocalStorage.getItem(BIOMETRIC_ENROLL_DISMISSED_KEY)
  return Array.isArray(ids) ? ids : []
}

function currentUserId () {
  const current = useUsersStore().current
  return (current && current.id) || null
}

// Aplica tokens + permisos de una respuesta de auth (password o refresh_token grant) al
// estado del store. Compartido por loginWithBiometrics, login() mantiene su propio flujo
// inline (refresco de la bandera remota de Tareas y luego resumeOfflineAuthOnLogin).
async function applyAuthResponses (context, responses) {
  const access_token = responses[0].access_token
  const gql_access_token = responses[1].access_token

  // `oauth/access_token` de api.binnacle.mx responde HTTP 200 incluso cuando el grant falla:
  // el error viaja en el body (`message`) y axios no lo trata como error. Sin esta validacion
  // la sesion quedaba a medias (solo GraphQL autenticado) y el flujo reportaba exito.
  // Va ANTES de setAccessTokens: si el grant fallo, no se escribe nada a LocalStorage.
  if (!access_token || !gql_access_token) {
    const detail = responses.map(r => r.message || r.error_description).filter(Boolean).join(' | ')
    throw new Error(`Auth response without access_token${detail ? `: ${detail}` : ''}`)
  }

  setAccessTokens(context, responses[0], responses[1])

  // Solo se sobrescribe si el grant devolvio un refresh_token nuevo. Ambos emisores rotan el
  // refresh_token en cada uso (Passport revoca el anterior, y el legacy tiene refreshTokenRotate
  // = true por default sin que config/oauth2.php lo desactive), asi que en el camino feliz
  // siempre llega uno nuevo; el fallback al valor previo es solo defensivo. Si se asigna
  // undefined, JSON.stringify omite la clave al re-guardar en el keystore y el enrolamiento se
  // corrompe de forma permanente.
  if (responses[0].refresh_token) context.refresh_token = responses[0].refresh_token
  if (responses[1].refresh_token) context.gql_refresh_token = responses[1].refresh_token

  const permissionsStore = usePermissionsStore()
  await PermissionsService.getAdminPermissions().then(permissions => {
    permissionsStore.setModulesPermissions(permissions)
  })
}

export default {
  login (user) {
    return new Promise((resolve, reject) => {
      this.status = 'loading'
      const api1 = UsersService.authenticate(user)
      const api2 = UsersService.authenticateGQL(user)

      Promise.all([api1, api2]).then(async responses => {
        // Auth success and set tokens
        setAccessTokens(this, responses[0], responses[1])
        // A diferencia de applyAuthResponses, aqui se asignan sin condicionar: el grant de
        // password siempre emite par nuevo, y este es el punto donde se siembra el enrolamiento.
        this.refresh_token = responses[0].refresh_token
        this.gql_refresh_token = responses[1].refresh_token

        const usersStore = useUsersStore()
        usersStore.current = responses[0].currentUser

        const organizationId = organizationIdOf(usersStore.current)

        // Bandera remota de Tareas: se refresca en CADA login y SIN gatear por la bandera (si se gateara,
        // apagarla desde el back no se podría deshacer sin release). Va ANTES del resume para que este ya
        // decida con el valor fresco. Fire-and-forget: nunca bloquea ni rompe el login.
        refreshRemoteOfflineFlag(organizationId)
          .then(() => resumeOfflineAuthOnLogin(), () => resumeOfflineAuthOnLogin())

        useGeolocationStore().refreshMockPolicy(organizationId)
          .catch(() => { /* best-effort: conserva el ultimo valor cacheado */ })

        // El boot solo llega a esto cuando ya habia sesion; tras un login en frio el listener del
        // resume se arma aqui, si no el kill switch de la politica no llegaria hasta el siguiente
        // arranque en frio.
        watchMockPolicyOnResume(organizationId)

        const permissionsStore = usePermissionsStore()

        // El currentUser del legacy no trae lo que solo arma users/user de graphql (p. ej. la
        // lista attendance_report_users, TKT-2283) y la sesion vivia con ese payload hasta
        // recargar. Se pide en paralelo con los permisos, mismo payload que ya usan el boot y el
        // login biometrico; si falla, se queda el del legacy y el login sigue.
        const graphUser = Promise.resolve().then(() => UsersService.fetchCurrent()).catch(error => {
          console.error(error)
          return null
        })

        await Promise.all([PermissionsService.getAdminPermissions(), graphUser]).then(([permissions, freshUser]) => {
          if (freshUser && Number(freshUser.id) === Number(usersStore.current?.id)) usersStore.current = freshUser
          permissionsStore.setModulesPermissions(permissions)
          // Ya tenemos user+permissions frescos aqui: los dejamos en la cache de boot entre
          // pestañas (bootCache.js) para que una pestaña nueva abierta justo despues de este
          // login (p. ej. el usuario abre varias de una vez) los aproveche sin re-pedirlos.
          writeBootCurrentCache(usersStore.current, permissions)
        }).finally(() => {
          resolve(responses[0])
        })
      }).catch(err => {
        this.status = ''
        reject(err)
      })
    })
  },
  // Re-authenticates using the refresh_token unlocked behind a biometric prompt, instead of a
  // username/password grant. Depends on graphql-api#1756 (the refresh_token grant needs its FK
  // cascade to not fail), so it is not backend-changes-free the way the original design assumed.
  loginWithBiometrics () {
    return new Promise((resolve, reject) => {
      this.status = 'loading'
      let username
      let stored = {}

      BiometricsService.verifyIdentity({
        title: i18n.global.t('auth.login.biometric-login-prompt-title'),
        description: i18n.global.t('auth.login.biometric-login-prompt-description')
      })
        .then(() => BiometricsService.getCredentials())
        .then(async credentials => {
          username = credentials.username

          try {
            stored = JSON.parse(credentials.password)
          } catch {
            await this.disableBiometricLogin()
            const error = new Error('Stored biometric credential is corrupted, re-enrollment required')
            error.reenrollmentRequired = true
            throw error
          }

          if (!stored.refresh_token || !stored.gql_refresh_token) {
            await this.disableBiometricLogin()
            const error = new Error('Stored biometric credential is incomplete, re-enrollment required')
            error.reenrollmentRequired = true
            throw error
          }

          return Promise.allSettled([
            UsersService.authenticateWithRefreshToken(stored.refresh_token),
            UsersService.authenticateGQLWithRefreshToken(stored.gql_refresh_token)
          ])
        })
        .then(async ([apiResult, gqlResult]) => {
          if (apiResult.status === 'fulfilled' && gqlResult.status === 'fulfilled') {
            try {
              await applyAuthResponses(this, [apiResult.value, gqlResult.value])
            } catch (applyError) {
              // Los dos POST respondieron 200, pero applyAuthResponses detecto un grant
              // funcionalmente rechazado (el legacy pre-#168 podia responder 200 con el error en
              // el body). Mismo criterio que un rechazo HTTP 4xx: el refresh_token guardado ya no
              // sirve, asi que se apaga el enrolamiento en vez de dejarlo fallando para siempre.
              await this.disableBiometricLogin()
              applyError.reenrollmentRequired = true
              throw applyError
            }

            // Passport y el legacy rotan el refresh_token al usarlo, asi que se re-guarda el nuevo
            // de cada lado (ver applyAuthResponses para el porque del fallback al valor previo).
            await persistBiometricTokens(
              username,
              this.refresh_token || stored.refresh_token,
              this.gql_refresh_token || stored.gql_refresh_token
            )

            const usersStore = useUsersStore()
            const currentUser = await UsersService.fetchCurrent()
            usersStore.current = currentUser

            resumeOfflineAuthOnLogin()

            resolve(currentUser)
            return
          }

          // Redencion parcial: ambos emisores rotan el refresh_token al usarlo, asi que si un lado
          // tuvo exito su token viejo ya quedo consumido del lado del servidor. Persistir de
          // inmediato el nuevo (junto con el del lado que fallo, sin tocar) evita perderlo en el
          // siguiente intento; sin esto, un exito parcial dejaba el enrolamiento muerto para
          // siempre (Promise.all descartaba el lado exitoso completo al rechazar).
          const rotated = {
            refresh_token: apiResult.status === 'fulfilled'
              ? (apiResult.value.refresh_token || stored.refresh_token)
              : stored.refresh_token,
            gql_refresh_token: gqlResult.status === 'fulfilled'
              ? (gqlResult.value.refresh_token || stored.gql_refresh_token)
              : stored.gql_refresh_token
          }

          const rejected = [apiResult, gqlResult].filter(result => result.status === 'rejected')

          if (rejected.some(r => isCredentialGrantError(r) && !isTransientRefreshTokenDecryptError(r))) {
            // Rechazo real del grant, no una falla transitoria: el lado que fallo no va a
            // funcionar solo con reintentar. Se apaga el enrolamiento para forzar un
            // re-enrolamiento limpio en el siguiente login con password, en vez de dejar un
            // boton biometrico que falla para siempre sin ruta de recuperacion.
            await this.disableBiometricLogin()
            const error = new Error('Biometric credential grant rejected, re-enrollment required')
            error.reenrollmentRequired = true
            throw error
          }

          await persistBiometricTokens(username, rotated.refresh_token, rotated.gql_refresh_token)
          throw new Error('Biometric login partially failed, please retry')
        })
        .catch(error => {
          this.status = ''
          reject(error)
        })
    })
  },
  async enrollBiometricLogin (username) {
    if (!this.refresh_token || !this.gql_refresh_token) {
      throw new Error('No active refresh token to enroll, call login() first')
    }

    await BiometricsService.verifyIdentity({
      title: i18n.global.t('auth.login.biometric-enroll-prompt-title'),
      description: i18n.global.t('auth.login.biometric-enroll-prompt-description')
    })
    await persistBiometricTokens(username, this.refresh_token, this.gql_refresh_token)
    LocalStorage.set('biometric_login_enabled', true)
    this.biometric_login_enabled = true
    this.clearBiometricEnrollmentDismissal()
  },
  async disableBiometricLogin () {
    LocalStorage.remove('biometric_login_enabled')
    this.biometric_login_enabled = false
    await BiometricsService.deleteCredentials()
  },
  fetchBiometricAvailability () {
    return BiometricsService.getAvailability()
  },
  isBiometricEnrollmentDismissed (userId = currentUserId()) {
    return !!userId && dismissedEnrollmentUserIds().includes(userId)
  },
  dismissBiometricEnrollment (userId = currentUserId()) {
    if (!userId || this.isBiometricEnrollmentDismissed(userId)) return
    LocalStorage.set(BIOMETRIC_ENROLL_DISMISSED_KEY, [...dismissedEnrollmentUserIds(), userId])
  },
  clearBiometricEnrollmentDismissal (userId = currentUserId()) {
    if (!userId) return
    LocalStorage.set(BIOMETRIC_ENROLL_DISMISSED_KEY, dismissedEnrollmentUserIds().filter(id => id !== userId))
  },
  // Tras un login con password, mantiene el enrolamiento biometrico del dispositivo sincronizado
  // con la sesion real. Si el usuario que acaba de entrar es el mismo que esta enrolado, re-guarda
  // sus refresh tokens frescos (auto-repara una redencion parcial previa, ver loginWithBiometrics).
  // Si es un usuario distinto (equipo compartido, otra huella/rostro registrado en el mismo
  // telefono), borra el enrolamiento anterior para que el nuevo usuario reciba el dialogo de
  // enrolamiento en vez de heredar en silencio el acceso biometrico de quien entro antes.
  async syncBiometricEnrollmentAfterLogin (username) {
    if (!this.biometric_login_enabled) return { shouldOfferEnrollment: !this.isBiometricEnrollmentDismissed() }

    let enrolledUsername = null
    try {
      const credentials = await BiometricsService.getCredentials()
      enrolledUsername = credentials.username
    } catch (error) {
      console.error(error)
    }

    if (enrolledUsername === username) {
      await persistBiometricTokens(username, this.refresh_token, this.gql_refresh_token)

      return { shouldOfferEnrollment: false }
    }

    await this.disableBiometricLogin()

    return { shouldOfferEnrollment: !this.isBiometricEnrollmentDismissed() }
  },
  logout () {
    LocalStorage.remove('gql_access_token')
    LocalStorage.remove('access_token')
    LocalStorage.remove('currentUser')
    // Device compartido: sin esto, una pestaña abierta justo tras el logout podria hidratar el
    // usuario VIEJO desde la cache de boot dentro de su ventana de 10s -- misma clase de riesgo
    // de exposicion cross-usuario que ya cuida wipeCatalogsForUserChange() para catalogos offline.
    clearBootCurrentCache()

    this.status = ''
    this.access_token = ''
    this.gql_access_token = ''
    this.refresh_token = ''
    this.gql_refresh_token = ''

    // Offline: detiene el worker periódico + listeners para no dejar timers vivos tras el logout
    // (fuga de recursos). Best-effort: nunca rompe el logout. El re-login lo re-arranca.
    // Además WIPE de catálogos [review Hugo #3]: en device compartido evita que el siguiente usuario
    // vea sitios/áreas cacheados de la organización del usuario que sale (exposición cross-org).
    try {
      stopSyncWorker()
      wipeCatalogsForUserChange().catch(() => { /* best-effort: no rompe el logout */ })
    } catch { /* best-effort */ }

    // unwatch PRIMERO: es una asignacion que no puede fallar, y si forgetMockPolicy reventara
    // (store sin pinia activa) el listener del resume seguiria refrescando la politica de la
    // organizacion que acaba de salir, que es justo la fuga que el sellado por organizacion cierra.
    unwatchMockPolicyOnResume()

    try {
      useGeolocationStore().forgetMockPolicy()
    } catch { /* best-effort: no rompe el logout */ }

    useGlobalStore().logout()
  },
  resetState () {
    useAuthStore().$reset()
  }
}
